<template>
  <div>
    <EquipmentEditHeader
      v-if="isAdmin"
      data-cy="hps-motor-search-view-edit-header"
    />
    <div v-if="requiredHp" class="mb-3">
      <div class="field-checkbox">
        <Checkbox
          v-model="enableFilter"
          :input-id="`enable-hp-filter-${requiredHp}`"
          binary
        />
        <label :for="`enable-hp-filter-${requiredHp}`"> Filter & Sort </label>
      </div>
      <div
        v-if="enableFilter && requiredHp && requiredHp < 100"
        class="mt-2 p-2"
        style="
          background: rgba(255, 193, 7, 0.06);
          border: 1px solid var(--yellow-500);
          border-radius: 4px;
        "
      >
        <small style="color: var(--yellow-400)">
          <i class="pi pi-info-circle mr-1"></i>
          No motors match required HP ({{ requiredHp }}). Showing smallest
          available motors—selections may trigger warnings.
        </small>
      </div>
    </div>
    <div v-if="noMotorsWithFilter" class="no-recommendation-message-container">
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No motors match this configuration.
        </p>
        <p class="no-recommendation-body">
          If you'd like to select a motor, please un-check
          <span class="no-recommendation-highlight">"Filter & Sort Motors"</span
          >.
        </p>
      </div>
    </div>
    <EquipmentTable
      v-else
      data-cy="hps-motor-search-view-table"
      @select="$emit('select', $event.data as Motors)"
    />
  </div>
</template>
<script setup lang="ts">
import { provide, ref, watch, computed } from "vue";
import { HpsMotors } from "@/composables/equipment/hpsMotors/hpsMotors";
import EquipmentEditHeader from "@/components/equipment/EquipmentEditHeader.vue";
import EquipmentTable from "@/components/equipment/EquipmentTable.vue";
import Checkbox from "primevue/checkbox";
import type { Motors } from "gql/api";

const props = defineProps<{
  isAdmin: boolean;
  requiredHp?: number | null;
  enableHpFilter?: boolean;
}>();

defineEmits<{
  (event: "select", data: Motors): void;
}>();

// Default to ON when requiredHp is provided and greater than 0
// If requiredHp exists and is valid, default to true (ignoring any false from parent)
// Only respect enableHpFilter if requiredHp doesn't exist or is 0
const enableFilter = ref(
  props.requiredHp && props.requiredHp > 0
    ? true
    : props.enableHpFilter ?? false
);

const motorSearch = HpsMotors(props.isAdmin, {
  requiredHP: props.requiredHp ?? 0,
  enableHPFilter: enableFilter.value
});

const sortedFilteredRows = computed(() => {
  const sorted = motorSearch.allRows.value || [];
  const baseFiltered = motorSearch.filteredRows.value || [];
  const filteredIds = new Set(baseFiltered.map((m: Motors) => m.id));
  return sorted.filter(m => filteredIds.has(m.id));
});

// When Filter and Sort is ON and no motors match the current configuration,
// show a helpful empty state instead of an empty table.
const noMotorsWithFilter = computed(() => {
  const filterOn = enableFilter.value;
  const count = motorSearch.allRows.value?.length ?? 0;
  const isLoading = motorSearch.fetching.value;

  // Only show the empty state when filter is ON, data has loaded, and we have 0 results
  return filterOn && !isLoading && count === 0;
});

const equipmentWithSortedMotors = {
  ...motorSearch,
  filteredRows: sortedFilteredRows
};

watch(
  () => props.enableHpFilter,
  newValue => {
    if (newValue !== undefined) {
      enableFilter.value = newValue;
    }
  }
);

watch(
  () => props.requiredHp,
  newValue => {
    if (newValue !== undefined && newValue !== null) {
      motorSearch.targetHP.value = newValue;
    }
  }
);

watch(
  enableFilter,
  newValue => {
    motorSearch.hpFilter.value = newValue;
  },
  { immediate: true }
);

provide("equipment", equipmentWithSortedMotors);
</script>
